import { ptToRem } from './text-style-options';

/**
 * Espaçamento do item (configs `spaceTop`, `spaceBottom`, `spaceLeft`, `spaceRight`): espaço
 * extra em volta do bloco no PDF, em pt, SOMADO ao respiro padrão entre os itens. Mesma
 * faixa do engine e do pdf-renderer (0 a 144); fora dela vale 0.
 */
export type SpacingSide = 'top' | 'bottom' | 'left' | 'right';

export const SPACING_SIDES: SpacingSide[] = ['top', 'bottom', 'left', 'right'];

export const SPACING_KEYS = {
    top: 'spaceTop',
    bottom: 'spaceBottom',
    left: 'spaceLeft',
    right: 'spaceRight',
} as const;

export type ItemSpacing = Record<SpacingSide, number>;

export const MAX_ITEM_SPACE_PT = 144;
export const ITEM_SPACE_STEP_PT = 2;

export const NO_SPACING: ItemSpacing = { top: 0, bottom: 0, left: 0, right: 0 };

export function parseItemSpace(value: unknown): number {
    return typeof value === 'number' && value > 0 && value <= MAX_ITEM_SPACE_PT ? value : 0;
}

export function itemSpacingOf(configs: Record<string, unknown> | null | undefined): ItemSpacing {
    return {
        top: parseItemSpace(configs?.[SPACING_KEYS.top]),
        bottom: parseItemSpace(configs?.[SPACING_KEYS.bottom]),
        left: parseItemSpace(configs?.[SPACING_KEYS.left]),
        right: parseItemSpace(configs?.[SPACING_KEYS.right]),
    };
}

export function hasSpacing(spacing: ItemSpacing): boolean {
    return SPACING_SIDES.some(side => spacing[side] > 0);
}

/** − e +: de 2 em 2, grudando no múltiplo de 2 mais perto. */
export function stepItemSpace(value: number, direction: 1 | -1): number {
    const snapped =
        direction > 0
            ? Math.floor(value / ITEM_SPACE_STEP_PT) * ITEM_SPACE_STEP_PT + ITEM_SPACE_STEP_PT
            : Math.ceil(value / ITEM_SPACE_STEP_PT) * ITEM_SPACE_STEP_PT - ITEM_SPACE_STEP_PT;
    return Math.min(MAX_ITEM_SPACE_PT, Math.max(0, snapped));
}

/** Digitação: número inteiro ou com meio ponto ("7,5"), dentro da faixa; senão null. */
export function parseSpaceInput(raw: string): number | null {
    const text = raw.trim().replace(',', '.');
    if (!/^\d+(\.\d+)?$/.test(text)) return null;
    const value = Math.round(Number(text) * 2) / 2;
    return value >= 0 && value <= MAX_ITEM_SPACE_PT ? value : null;
}

/**
 * Lados em rem para as variáveis do card no editor (a folha do editor tem a largura do
 * PDF, então o recuo lê igual). Lado sem espaço vale null: a variável some e o CSS cai no 0.
 */
export function spacingRem(spacing: ItemSpacing): Record<SpacingSide, string | null> {
    const rem = (pt: number) => (pt > 0 ? ptToRem(pt) : null);
    return {
        top: rem(spacing.top),
        bottom: rem(spacing.bottom),
        left: rem(spacing.left),
        right: rem(spacing.right),
    };
}
